import React from 'react';

const ValueIcon = ({ iconClass }) => (
  <div className="efis-value-icon">
    <i className={iconClass} aria-hidden="true"></i>
  </div>
);

function About() {
  const values = [
    { title: 'International Solidarity', desc: 'Promoting cooperation, support, and meaningful connections among individuals, communities, and nations worldwide.', icon: 'fas fa-handshake' },
    { title: 'Global Citizenship', desc: 'Encouraging awareness of collective responsibility for humanity and active participation in building a fair and sustainable world.', icon: 'fas fa-globe' },
    { title: 'Peace and Tolerance', desc: 'Cultivating dialogue, understanding, and coexistence while reducing prejudice and stereotypes.', icon: 'fas fa-hand-peace' },
    { title: 'Mutual Respect', desc: 'Valuing cultural diversity and fostering harmonious interactions across borders.', icon: 'fas fa-users' },
    { title: 'Equality and Justice', desc: 'Supporting self-determination, equal opportunities, and fairness for individuals and communities worldwide.', icon: 'fas fa-balance-scale' },
    { title: 'Sustainability', desc: 'Promoting environmental responsibility, protection of natural heritage, and long-term well-being.', icon: 'fas fa-leaf' },
    { title: 'Social Responsibility', desc: 'Encouraging civic engagement, volunteering, and actions that benefit society.', icon: 'fas fa-heart' },
    { title: 'Human Rights', desc: 'Advocating for human dignity, fundamental rights, and the rule of law.', icon: 'fas fa-shield-alt' }
  ];

  const objectives = [
    { title: 'Promote International Solidarity and Cultural Exchange', desc: 'Facilitate direct interaction among youth from different regions, especially between the Global North and South, to foster understanding, collaboration, and shared human values.' },
    { title: 'Empower Youth and Civic Engagement', desc: 'Support education, volunteering, training, and professional development initiatives to strengthen young people’s autonomy, leadership, and social participation.' },
    { title: 'Advance Global Citizenship', desc: 'Raise awareness of collective responsibility, social justice, and equality, encouraging inclusive participation in building a global society.' },
    { title: 'Support Development in Developing Countries', desc: 'Implement programs that improve access to education, healthcare, professional skills, and economic opportunities to reduce social, economic, and cultural disparities.' },
    { title: 'Promote Sustainability and Environmental Awareness', desc: 'Encourage ecological responsibility, animal rights, and sustainable tourism and development practices.' },
    { title: 'Encourage Inclusive Economic Models', desc: 'Support social entrepreneurship, solidarity economy, and innovative projects fostering sustainable growth.' },
    { title: 'Develop Leadership and Conflict Resolution Skills', desc: 'Provide coaching, workshops, and tailored learning opportunities to strengthen communication, negotiation, and leadership abilities.' },
    { title: 'Uphold Human Rights and Self-Determination', desc: 'Advocate for freedom, dignity, and equality through awareness campaigns, partnerships, and support for NGOs promoting democracy and the rule of law.' },
    { title: 'Foster Intercultural Understanding', desc: 'Organize exchanges, volunteer projects, and programs that break down prejudices and build respect among different cultures.' }
  ];

  return (
    <>
      {/* Hero Start */}
      <div className="slider-area2">
        <div className="slider-height2 d-flex align-items-center">
          <div className="container">
            <div className="row">
              <div className="col-xl-12">
                <div className="hero-cap hero-cap2 pt-20 text-center">
                  <h2>About Us</h2>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Introduction Paragraphs — keep standard spacing after hero */}
      <section className="about-low-area section-padding30 about-intro-section" style={{background: '#fff'}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-md-12">
              <div className="about-caption text-center">
                <div className="section-tittle mb-35">
                  <span>Who We Are</span>
                  <h2>Europe Foundation for International Solidarity</h2>
                </div>
                <div style={{fontSize: '16px', lineHeight: '1.8', color: '#4b5563', textAlign: 'left'}}>
                  <p style={{marginBottom: '20px'}}>
                    Europe Foundation for International Solidarity (Poland) is a non-profit organization dedicated to promoting international solidarity, intercultural dialogue, and global citizenship. Established in Siedlce, Poland, the Foundation connects citizens and young people from across Europe and other regions of the world through international mobility and cooperation programs, particularly through the Erasmus+ programme supported by the European Union. These initiatives enable participants to work together around shared values including human rights, peace, equality, and sustainable development.
                  </p>
                  <p style={{marginBottom: '20px'}}>
                    The Foundation operates as a development-oriented NGO and seeks to expand its activities through additional European and international cooperation frameworks. While currently implementing projects under Erasmus+, EFIS aspires to develop future initiatives aligned with the principles and objectives promoted by the United Nations and Poland’s official development cooperation program (Polish Aid).
                  </p>
                  <p>
                    Through these actions and strategic ambitions, the Foundation contributes to and aims to further strengthen sustainable development, social inclusion, and equal opportunities at local, regional, and global levels.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="service-area section-padding30 section-compact about-values-section" style={{background: '#f8fafc', borderTop: '1px solid #e2e8f0'}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-6 col-lg-7 col-md-10">
              <div className="section-tittle text-center mb-80">
                <span>Core Guidelines</span>
                <h2>Our Values</h2>
                <p>The Foundation is guided by core values that define its vision and approach:</p>
              </div>
            </div>
          </div>
          <div className="row">
            {values.map((val, index) => (
              <div key={index} className="col-lg-3 col-md-6 col-sm-6 mb-30">
                <div className="single-cat h-100 text-center p-40 efis-value-card" style={{border: '1px solid #e2e8f0', borderRadius: '12px', background: '#fff', boxShadow: '0 5px 15px rgba(0,0,0,0.02)'}}>
                  <ValueIcon iconClass={val.icon} />
                  <div className="cat-cap">
                    <h5 style={{fontWeight: '700', marginBottom: '10px', color: 'var(--primary-blue)'}}>{val.title}</h5>
                    <p style={{fontSize: '13px', color: '#64748b', lineHeight: '1.6'}}>{val.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="objectives-section section-padding30 section-compact" style={{background: '#fff', borderTop: '1px solid #e2e8f0'}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-lg-10">
              <div className="section-tittle text-center mb-80">
                <span>Action Plan</span>
                <h2>Our Objectives</h2>
                <p>The Foundation works to translate its values into concrete actions through the following objectives:</p>
              </div>
            </div>
          </div>
          <div className="row">
            {objectives.map((obj, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-30">
                <div className="card h-100 efis-objective-card" style={{border: 'none', overflow: 'hidden'}}>
                  <div className="card-body p-4 d-flex align-items-start">
                    <div className="efis-objective-number" aria-hidden="true">{index + 1}</div>
                    <div style={{ marginLeft: '16px' }}>
                      <h5 style={{fontWeight: '700', color: 'var(--primary-blue)', marginBottom: '10px', fontSize: '15px', lineHeight: 1.35}}>{obj.title}</h5>
                      <p style={{fontSize: '13.5px', color: '#4b5563', lineHeight: '1.6', marginBottom: 0}}>{obj.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="about-low-area section-padding30 section-compact" style={{background: '#f8fafc', borderTop: '1px solid #e2e8f0'}}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-12">
              <div className="about-caption">
                <div className="section-tittle mb-35">
                  <span>Who We Are</span>
                  <h2>Our Team</h2>
                </div>
                <p style={{fontSize: '16px', lineHeight: '1.8', color: '#4b5563', marginBottom: '20px'}}>
                  Our team at EFIS comes from diverse cultural and professional backgrounds and has gained extensive experience in the past through coordinating programs linked to the United Nations and the European Union, as well as working in international organizations across different countries.
                </p>
                <p style={{fontSize: '16px', lineHeight: '1.8', color: '#4b5563'}}>
                  They design and implement programs that connect young people from around the world, promote development initiatives and volunteering, and foster intercultural understanding. Our team is committed to supporting participants in building meaningful relationships and making a positive impact in their communities and beyond.
                </p>
              </div>
            </div>
            <div className="col-lg-6 col-md-12">
              <div className="about-img" style={{position: 'relative', overflow: 'hidden', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)'}}>
                <img src="https://thumbs.dreamstime.com/b/happy-volunteer-portrait-people-park-charity-event-community-service-teamwork-volunteering-men-women-smile-332491971.jpg?w=992" alt="Our Team" style={{width: '100%', objectFit: 'cover'}} onError={(e) => { e.target.src = '/assets/img/gallery/about1.png'; }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Volunteers Section */}
      <section className="about-low-area section-padding30 section-compact" style={{background: '#fff', borderTop: '1px solid #e2e8f0'}}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-12 order-lg-2">
              <div className="about-caption">
                <div className="section-tittle mb-35">
                  <span>The Driving Force</span>
                  <h2>Our Volunteers</h2>
                </div>
                <p style={{fontSize: '16px', lineHeight: '1.8', color: '#4b5563', marginBottom: '20px'}}>
                  Our volunteers are the driving force of EFIS. They participate in international mobility programs, exchanges, and community initiatives, helping to spread our values of human rights, equality, and sustainable development.
                </p>
                <p style={{fontSize: '16px', lineHeight: '1.8', color: '#4b5563'}}>
                  By engaging with EFIS, volunteers build friendships across borders and contribute to creating a world based on shared human values.
                </p>
              </div>
            </div>
            <div className="col-lg-6 col-md-12 order-lg-1">
              <div className="about-img" style={{position: 'relative', overflow: 'hidden', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)'}}>
                <img src="https://media.istockphoto.com/id/504530736/photo/group-of-teenagers-volunteer-happiness.jpg?s=1024x1024&w=is&k=20&c=zLCMuEff7bcY6Z9-OSwGQ7PV8Xo_pCaMs2YvD6GCWGQ=   " alt="Our Volunteers" style={{width: '100%', objectFit: 'cover'}} onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?auto=format&fit=crop&w=800&q=80'; }} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default About;
